import React, { useCallback, useEffect } from "react";
import { inject, observer } from "mobx-react";
import { ADD_ROW } from "constants/translates";
import { DateRangeDate, IObjectMap } from "interfaces/common";
import {
  CONTRACT_ACTIVE_COLUMNS,
  CONTRACT_ORDER_COLUMNS,
} from "constants/settings";
import { Form } from "antd";
import ActionButton from "../components/declarants/groupButton";
import TableWrapper from "../components/declarants/tableWrapper";
import Table from "../components/common/Table";
import DateRange from "../components/declarants/dateRange";
import Search from "../components/declarants/search";
import Settings from "../components/declarants/Settings";
import COLUMNS from "../components/tables/contractsTable/config";
import updateSortingAndFiltersOfColumns from "../helpers/filters";
import { ColumnsType } from "antd/lib/table/interface";
import SettingsStore from "stores/SettingsStore";
import UserStore from "stores/UserStore";
import CollapseHeader from "components/CollapseHeader";
import Card from "components/Card/Card";
import ee from "services/EventEmitter";
import { ADD_NEW_CONTRACTS_ROW } from "constants/events";
import ContractsStore from "stores/ContractsStore";
import { Contracts } from "models/Contracts";

let isLoaded = false;

const temporaryLinesCheck = (data: Contracts[]) => {
  let withTemporary = false;

  data?.forEach((row) => {
    if (row?.temporary) {
      withTemporary = true;
    }
  });

  return withTemporary;
};

const ContractsPage: React.FC<{
  settingsStore: SettingsStore;
  userStore: UserStore;
}> = ({ settingsStore, userStore }) => {
  const [form] = Form.useForm();
  const disabledAddButton = temporaryLinesCheck(
    ContractsStore.data as Contracts[]
  );

  useEffect(() => {
    if (isLoaded) return;

    ContractsStore.load({}, true);
    isLoaded = true;
  }, []);

  const onLoadHandler = useCallback(
    (parameters?: IObjectMap, forceUpdate?: boolean): void => {
      ContractsStore.load(
        {
          ...parameters,
          searchPhrase: ContractsStore.parameters.searchPhrase,
          start_date: ContractsStore.parameters.start_date,
          end_date: ContractsStore.parameters.end_date,
        },
        forceUpdate
      );
    },
    []
  );

  const handleAdd = () => {
    ee.emit(ADD_NEW_CONTRACTS_ROW, "");
  };

  useEffect(() => {
    settingsStore?.setColumns(
      updateSortingAndFiltersOfColumns(
        userStore.authorizedOnly(COLUMNS(form, userStore)) ||
          COLUMNS(form, userStore),
        ContractsStore.parameters
      ),
      CONTRACT_ORDER_COLUMNS,
      CONTRACT_ACTIVE_COLUMNS
    );
  }, [
    form,
    settingsStore,
    settingsStore.settings,
    userStore,
    userStore.user,
    settingsStore.isEnabledReorder,
    ContractsStore.parameters,
    ContractsStore.editingKeys,
    ContractsStore.companyNameOptions,
    ContractsStore.statusOptions,
    ContractsStore.countryOptions,
  ]);

  const getCheckboxProps = (record: any) => {
    const isVisible = record?.children
      ? { display: "block" }
      : { display: "none" };

    return {
      style: isVisible,
    };
  };

  return (
    <>
      <CollapseHeader>
        <DateRange
          store={ContractsStore}
          startDate={ContractsStore.parameters.start_date as DateRangeDate}
          endDate={ContractsStore.parameters.end_date as DateRangeDate}
          allowClear={true}
        />
        <Search store={ContractsStore} />
        <div />
      </CollapseHeader>
      <Card>
        <Card.Header>
          <ActionButton
            onClick={handleAdd}
            text={ADD_ROW}
            disabled={disabledAddButton}
          />
          <Settings reorderable />
        </Card.Header>
        <TableWrapper
          enableScrollBar={settingsStore.isEnabledScrollbar}
          className="contracts-table"
        >
          <Form form={form} component={false}>
            <Table
              key="loading-done"
              columns={settingsStore.activeColumns as ColumnsType<Contracts>}
              settingsStore={settingsStore}
              data={ContractsStore.data}
              onLoad={onLoadHandler}
              hasMoreItems={ContractsStore.hasMoreItems}
              isLoading={ContractsStore.isLoadingTableData}
              rowClassName={(record) => {
                if ((record as Contracts).ban_on_registration) {
                  return "contracts-ban";
                }

                return "";
              }}
              checkStrictly={true}
              expandableConfig={{
                expandRowByClick: false,
                indentSize: 10,
              }}
              getCheckboxProps={getCheckboxProps}
            />
          </Form>
        </TableWrapper>
      </Card>
    </>
  );
};

export default inject(({ store }) => ({
  settingsStore: store.settingsStore,
  userStore: store.userStore,
}))(observer(ContractsPage));
